import { Link } from 'react-router-dom';
import SEO from '@/components/base/SEO';
import ServicePageLayout from '@/components/base/ServicePageLayout';

const breadcrumb = [
  { label: 'Accueil', to: '/' },
  { label: 'Réparation baie coulissante Hérault' },
];

const jsonLdService = {
  '@context': 'https://schema.org',
  '@type': 'Service',
  name: 'Réparation de baie vitrée coulissante dans l\'Hérault',
  provider: { '@id': 'https://heraultroulettesbaiesvitrees.fr/#business' },
  areaServed: { '@type': 'AdministrativeArea', name: 'Hérault' },
  description: 'Déblocage, remplacement de roulettes et galets, réparation de rail, réglage, crémone et serrure, remplacement complet de baie coulissante aluminium dans l\'Hérault.',
  offers: { '@type': 'Offer', price: '0', priceCurrency: 'EUR', description: 'Devis gratuit' },
};

const jsonLdFAQ = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    { '@type': 'Question', name: 'Quels types de pannes sur une baie coulissante réparez-vous ?', acceptedAnswer: { '@type': 'Answer', text: 'Nous intervenons sur tous les types de pannes : baie qui coince ou déraille, roulettes et galets usés, rail encrassé ou déformé, serrure et crémone défectueuses, vitrage cassé ou embué, et remplacement complet de la baie coulissante. Nos techniciens sont formés sur toutes les marques aluminium du marché.' } },
    { '@type': 'Question', name: 'Intervenez-vous sur les baies coulissantes de marque Technal et Sepalumic ?', acceptedAnswer: { '@type': 'Answer', text: 'Oui, nous réparons les baies coulissantes de toutes marques : Technal, Sepalumic, Schüco, K-Line, Wicona, Kawneer, SAPA et bien d\'autres. Nous disposons des pièces détachées compatibles et nos techniciens connaissent les spécificités de chaque fabricant.' } },
    { '@type': 'Question', name: 'Combien coûte la réparation d\'une baie coulissante dans l\'Hérault ?', acceptedAnswer: { '@type': 'Answer', text: 'Le coût dépend de la nature de la panne. Un changement de roulettes ou un réglage est généralement plus abordable qu\'un remplacement complet de baie. Nous proposons un devis gratuit et sans engagement : notre technicien se déplace, diagnostique la panne et vous remet un devis détaillé avant toute intervention.' } },
  ],
};

const cardGrids = [
  {
    title: 'Nos services de réparation de baie coulissante',
    subtitle: 'Une baie vitrée coulissante est un élément central de votre habitat : elle apporte luminosité, accès à l\'extérieur et isolation thermique. Lorsqu\'elle tombe en panne — qu\'elle coince, qu\'elle déraille ou que le vitrage est endommagé — c\'est une source d\'inconfort et parfois même un risque pour la sécurité de votre logement. DFPRO Hérault intervient rapidement sur tous les types de pannes, à Montpellier, dans sa métropole et sur tout le littoral héraultais.',
    cards: [
      {
        icon: 'ri-tools-line',
        title: 'Déblocage et diagnostic',
        description: 'Votre baie est bloquée ? Nos techniciens réalisent un diagnostic complet pour identifier l\'origine de la panne et procèdent au déblocage.',
        link: '/baie-coulissante-qui-coince-herault/',
      },
      {
        icon: 'ri-settings-3-line',
        title: 'Roulettes et galets',
        description: 'Pièces d\'usure les plus fréquentes. Nous remplaçons les roulettes et galets par des pièces compatibles, toutes marques.',
        link: '/changer-roulettes-galets-baie-coulissante-herault/',
      },
      {
        icon: 'ri-layout-masonry-line',
        title: 'Réparation du rail',
        description: 'Rail encrassé, déformé ou corrodé ? Nettoyage, redressement ou remplacement pour un coulissement parfait.',
        link: '/reparation-rail-baie-coulissante-herault/',
      },
      {
        icon: 'ri-equalizer-line',
        title: 'Réglage de baie',
        description: 'Mauvais alignement des vantaux, hauteur mal ajustée ou compression de joint insuffisante ? Nous réglons votre baie.',
        link: '/reglage-baie-coulissante-herault/',
      },
      {
        icon: 'ri-lock-line',
        title: 'Serrure et crémone',
        description: 'La sécurité de votre habitat passe par une serrure en parfait état. Nous réparons et remplaçons crémones et poignées.',
        link: '/serrure-cremone-baie-coulissante-herault/',
      },
      {
        icon: 'ri-arrow-left-right-line',
        title: 'Remplacement complet',
        description: 'Quand la réparation n\'est plus suffisante, nous remplaçons intégralement votre baie par un modèle aluminium sur mesure.',
        link: '/remplacement-baie-coulissante-herault/',
      },
    ],
  },
];

const contentBlocks = [
  {
    image: '/images/baie-pilier-block2.jpg',
    imageAlt: 'Technicien DFPRO réparant une baie coulissante dans l\'Hérault',
    imagePosition: 'right' as const,
    children: (
      <>
        <h2>Baies aluminium toutes marques</h2>
        <p>Nous intervenons sur l'ensemble des marques de menuiseries aluminium présentes sur le marché français : Technal, Sepalumic, Schüco, K-Line, Wicona, Kawneer, SAPA et bien d'autres. Chaque fabricant a ses spécificités techniques, ses systèmes de roulettes, ses profils de rail. Nos techniciens sont formés à ces différentes configurations et disposent d'un large stock de pièces détachées pour intervenir efficacement, quel que soit le modèle de votre baie coulissante.</p>
      </>
    ),
  },
];

const fullWidthSections = [
  <>
    <h2>Pannes courantes des baies coulissantes</h2>
    <p>Les baies coulissantes sont des équipements robustes, mais soumis à des sollicitations quotidiennes. Voici les pannes les plus fréquentes que nous rencontrons dans l'Hérault, du littoral à l'arrière-pays :</p>
    <ul>
      <li><strong>Baie dure à ouvrir ou fermer</strong> : roulettes usées, rail encrassé ou désalignement du vantail. Consultez notre page dédiée : <Link to="/baie-coulissante-qui-coince-herault/" className="text-accent-500 font-semibold hover:underline">baie coulissante qui coince dans l'Hérault</Link>.</li>
      <li><strong>Bruit de raclement ou grincement</strong> : galets à remplacer ou rail nécessitant un nettoyage approfondi.</li>
      <li><strong>Vitrage cassé ou embué</strong> : remplacement du <Link to="/double-vitrage-baie-coulissante-herault/" className="text-accent-500 font-semibold hover:underline">double vitrage de baie coulissante</Link> pour restaurer l'isolation thermique et phonique.</li>
      <li><strong>Serrure qui ne ferme plus</strong> : mécanisme de crémone défectueux ou crochet de verrouillage usé.</li>
    </ul>
  </>,
  <>
    <h2>Comment se déroule une intervention DFPRO Hérault ?</h2>
    <p>Notre processus de réparation est conçu pour être simple, transparent et rapide. Lorsque vous nous contactez au 09 80 80 22 42, nous échangeons sur votre panne et planifions un déplacement. Notre technicien se rend à votre domicile ou dans vos locaux — à Montpellier, Sète, Agde, Béziers, Frontignan, La Grande-Motte ou toute autre commune de l'Hérault. Il réalise un diagnostic précis, identifie les pièces à remplacer, et vous remet un devis gratuit et sans engagement. Une fois votre accord obtenu, l'intervention est programmée sous 24 à 48h. Chaque réparation est couverte par notre garantie décennale.</p>
    <h2>Zone d'intervention dans l'Hérault</h2>
    <p>DFPRO Hérault couvre l'ensemble du département : Montpellier et sa métropole, le littoral de Sète à La Grande-Motte en passant par Agde, Béziers, Frontignan, Palavas-les-Flots, et toutes les communes environnantes. Quel que soit votre secteur dans l'Hérault, nos techniciens se déplacent rapidement pour réparer votre baie coulissante.</p>
  </>,
];

export default function PilierBaieCoulissante() {
  return (
    <>
      <SEO
        title="Réparation baie coulissante Hérault | Roulettes | DFPRO"
        description="Baie coulissante qui coince, déraille, roulettes ou galets usés dans l'Hérault ? Réparation 24-48h, 7j/7. Devis gratuit, garantie 10 ans. Appelez DFPRO Hérault."
        canonicalUrl="https://heraultroulettesbaiesvitrees.fr/reparation-baie-coulissante-herault/"
      />
      <ServicePageLayout
        title="Réparation de baie vitrée coulissante dans l'Hérault"
        subtitle="Déblocage, roulettes, rail, réglage, serrure : nos techniciens réparent votre baie coulissante sous 24 à 48h dans tout l'Hérault. Devis gratuit, garantie 10 ans."
        breadcrumb={breadcrumb}
        jsonLdService={jsonLdService}
        jsonLdFAQ={jsonLdFAQ}
        heroImage="/images/baie-pilier-hero.jpg"
        heroImageAlt="Réparation de baie coulissante aluminium dans l'Hérault"
        contentBlocks={contentBlocks}
        cardGrids={cardGrids}
        fullWidthSections={fullWidthSections}
      />
    </>
  );
}